import React from 'react';
import { Device } from '../../lib/types';
import { isFosterContactComplete } from '../../lib/utils';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import BatteryIndicator from './BatteryIndicator';
import DeviceStatusCell from './DeviceStatusCell';
import PetNameCell from './PetNameCell';
import styles from './Devices.module.scss';

interface DeviceRowProps {
  device: Device;
  onAddPet: (device: Device) => void;
  onTransferFoster: (device: Device) => void;
  onRecall: (device: Device) => void;
  onTransferAdopter: (device: Device) => void;
  /** One-click adopt-out using the foster household's info (Fosters table only). */
  onAdoptFromFoster?: (device: Device) => void;
  onSendSms: (device: Device) => void;
  /** Pet IDs whose adoption transfer is complete — hides rescue mutation actions. */
  adoptedPetIds?: Set<string>;
  /** True while the adopted-pet lookup is in flight — gate actions optimistically. */
  adoptedLoading?: boolean;
  /** Show the Holder cell (kept in sync with the table's Holder column). */
  showHolder?: boolean;
}

export default function DeviceRow({
  device,
  onAddPet,
  onTransferFoster,
  onRecall,
  onTransferAdopter,
  onAdoptFromFoster,
  onSendSms,
  adoptedPetIds,
  adoptedLoading,
  showHolder = true,
}: DeviceRowProps) {
  const isOnline = device.status === 'online';
  const petAdopted = adoptedLoading || !!adoptedPetIds?.has(device._petGraphqlId);

  // Setup pills
  const szVariant = device.safeZone ? 'success' : 'error';
  const wifiVariant = device.wifiConfigured ? 'success' : 'error';

  // Actions depend on holder type and dog assignment
  let actions: React.ReactNode;
  if (device.holderType === 'rescue' && !device.petName) {
    actions = (
      <Button variant="green" size="sm" onClick={() => onAddPet(device)}>
        Add Pet
      </Button>
    );
  } else if (device.holderType === 'foster') {
    // Adopt-out to the foster household in one click. Disabled when the foster's
    // contact info is incomplete, since the transfer sends the adopter an email.
    const canAdoptFromFoster = isFosterContactComplete(device);
    actions = (
      <div className={styles.rowActions}>
        <Button variant="ghost" size="sm" onClick={() => onSendSms(device)}>
          Send SMS
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onRecall(device)}>
          Recall
        </Button>
        <Button variant="green" size="sm" disabled={!canAdoptFromFoster} onClick={() => onAdoptFromFoster?.(device)}>
          Adopt
        </Button>
      </div>
    );
  } else if (petAdopted) {
    actions = <span className={styles.textMuted}>&mdash;</span>;
  } else {
    actions = (
      <div className={styles.rowActions}>
        <Button variant="green" size="sm" onClick={() => onTransferAdopter(device)}>
          Adopt
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onTransferFoster(device)}>
          Foster
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onRecall(device)}>
          Unlink Device
        </Button>
      </div>
    );
  }

  return (
    <tr>
      {showHolder && (
        <td>
          <span className={styles.holderName}>{device.holderName}</span>
        </td>
      )}
      <td>
        {device.petName ? (
          <PetNameCell name={device.petName} photoThumbUrl={device.petPhotoThumbUrl} />
        ) : (
          <span className={styles.textMuted}>Unassigned</span>
        )}
      </td>
      <td>
        <div className={styles.deviceId}>{device.id}</div>
        <div className={styles.deviceType}>{device.type}</div>
      </td>
      <td>
        <div className={styles.setupPills}>
          <StatusBadge variant={szVariant}>Safe Zone</StatusBadge>
          <StatusBadge variant={wifiVariant}>WiFi</StatusBadge>
        </div>
      </td>
      <td>
        <DeviceStatusCell isOnline={isOnline} lastSeen={device.lastSeen} />
      </td>
      <td>
        <BatteryIndicator battery={device.battery} />
      </td>
      <td>{actions}</td>
    </tr>
  );
}
